// Follow Ups tracker — one row per outreach thread that's out with an external
// party and still open: when it was last sent, how many days have passed, and
// when the next chase is due. Pure — the caller passes the jobs, the live
// receipts and the "as of" date (PROXY_SEED_TODAY).
import type { MaterialReceiptRow } from '../types/api';
import type { ProxyJob, ProxyFollowUpRecord } from '../data/mockProxyJobs';
import type { ProxyInboxEmail } from '../data/mockProxyEmails';
import { resolveProxyJob } from './proxyQueue';
import { classifyEmail } from './proxyEmailClassifier';

export type OutreachKind = 'material' | 'shortage' | 'contest';

// followUpHistory types that are a chase sent to an external party.
export const OUTREACH_KIND: Record<string, OutreachKind> = {
  'FU-American': 'material',
  'FU-Canadian': 'material',
  'FU-Broadridge': 'material',
  'FU-Issuer': 'material',
  'FU-Red': 'material',
  red_followup: 'material',
  'Material-Shortage': 'shortage',
  shortage_outreach: 'shortage',
  contest_outreach: 'contest',
};

export const OUTREACH_KIND_LABEL: Record<OutreachKind, string> = {
  material: 'Material follow-up',
  shortage: 'Shortage follow-up',
  contest: 'Contest form',
};

// ICT-7: a job followed up in the last 4 days stays off the next report, so
// the next chase is due 4 days after the last one. The client confirmed teams
// don't chase daily — shortages included.
export const FOLLOW_UP_INTERVAL_DAYS = 4;
// ICT-3: follow up on an unreturned contest form three times over one week.
export const CONTEST_INTERVAL_DAYS = 2;

// Replied, Bounced and On hold mean the ball isn't in our court for a chase:
// someone answered (there's a task for it), the last chase didn't arrive
// (there's an exception for it), or Discovery is updating the job.
export type TrackerStatus = 'Overdue' | 'Due' | 'Waiting' | 'Replied' | 'Bounced' | 'On hold';

export interface TrackerRow {
  id: string;
  job: ProxyJob;
  kind: OutreachKind;
  attempts: number;
  lastSent: string;      // YYYY-MM-DD
  lastRecipient: string;
  daysElapsed: number;
  nextAction: string;    // YYYY-MM-DD
  status: TrackerStatus;
  note?: string;         // why it's Replied / Bounced / On hold
  responseEmailId?: string;
}

function dateOf(stamp: string): string {
  return stamp.slice(0, 10);
}

function dayDiff(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function threadOpen(job: ProxyJob, kind: OutreachKind, receipts: MaterialReceiptRow[] | null): boolean {
  if (job.status === 'ABORTED') return false;
  if (kind === 'contest') return !!job.contest && !['RECEIVED', 'SENT_TO_DISCOVERY'].includes(job.contest.formStatus);
  const resolved = resolveProxyJob(job, receipts);
  return resolved.effectiveStatus === 'ACTIVE' && job.materialStatus !== 'Received';
}

const STATUS_ORDER: Record<TrackerStatus, number> = { Overdue: 0, Due: 1, Replied: 2, Bounced: 3, Waiting: 4, 'On hold': 5 };

// The latest answer to this thread since the last chase, if any.
function responseTo(job: ProxyJob, kind: OutreachKind, lastSent: string, emails: ProxyInboxEmail[]): ProxyInboxEmail | undefined {
  const categories = kind === 'contest' ? ['PROXY_CONTEST'] : ['FOLLOW_UP_REPLY', 'BOUNCE'];
  return emails
    .filter(e => e.relatedJobId === job.jobId && e.date >= lastSent && categories.includes(classifyEmail(e).category || ''))
    .sort((a, b) => b.date.localeCompare(a.date))[0];
}

// When a person dealt with that answer: the reply logged in MIC, the
// bounced contact sent for checking, or the contest form moved on.
function handledAt(job: ProxyJob, response: ProxyInboxEmail): string | null {
  const category = classifyEmail(response).category;
  const types = category === 'BOUNCE' ? ['email_bounce_verify']
    : category === 'FOLLOW_UP_REPLY' ? ['reply_actioned']
      : ['signature_event', 'contest_form_received', 'contest_declined', 'discovery_handoff'];
  // Matched to this email when the record says which one it handled.
  const done = job.followUpHistory.filter(r => types.includes(r.type)
    && (r.sourceEmailId ? r.sourceEmailId === response.id : dateOf(r.timestamp) >= response.date));
  return done.length ? dateOf(done[done.length - 1].timestamp) : null;
}

export function buildFollowUpTracker(jobs: ProxyJob[], receipts: MaterialReceiptRow[] | null, today: string, emails: ProxyInboxEmail[] = []): TrackerRow[] {
  const rows: TrackerRow[] = [];
  for (const job of jobs) {
    const byKind = new Map<OutreachKind, ProxyFollowUpRecord[]>();
    for (const record of job.followUpHistory) {
      const kind = OUTREACH_KIND[record.type];
      if (kind) byKind.set(kind, [...(byKind.get(kind) || []), record]);
    }
    byKind.forEach((records, kind) => {
      if (!threadOpen(job, kind, receipts)) return;
      const last = records.reduce((a, b) => (b.timestamp > a.timestamp ? b : a));
      const lastSent = dateOf(last.timestamp);
      const response = responseTo(job, kind, lastSent, emails);
      const handled = response ? handledAt(job, response) : null;
      // A handled answer restarts the clock from the day it was dealt with.
      const nextAction = addDays(handled ?? lastSent, kind === 'contest' ? CONTEST_INTERVAL_DAYS : FOLLOW_UP_INTERVAL_DAYS);
      const untilNext = dayDiff(today, nextAction);
      let status: TrackerStatus = untilNext < 0 ? 'Overdue' : untilNext === 0 ? 'Due' : 'Waiting';
      let note: string | undefined;
      if (kind !== 'contest' && job.heldForDiscovery) {
        status = 'On hold';
        note = `${job.heldForDiscovery}. Waiting on Discovery.`;
      } else if (response && !handled) {
        const bounced = classifyEmail(response).category === 'BOUNCE';
        status = bounced ? 'Bounced' : 'Replied';
        note = bounced ? 'The last chase bounced. Confirm the contact in Exceptions.' : `${response.from} replied on ${response.date}.`;
      }
      rows.push({
        id: `${job.jobId}:${kind}`,
        job,
        kind,
        attempts: records.length,
        lastSent,
        lastRecipient: last.recipientEmail,
        daysElapsed: dayDiff(lastSent, today),
        nextAction,
        status,
        note,
        responseEmailId: response && !handled ? response.id : undefined,
      });
    });
  }
  return rows.sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.nextAction.localeCompare(b.nextAction));
}

export function isTrackerRowDue(row: TrackerRow): boolean {
  return row.status === 'Overdue' || row.status === 'Due';
}
